import { useCallback, useEffect, useState } from 'react';
import useDebounce from 'app/shareable/hooks/useDebounce';
import useReservationsAPI from 'app/hooks/reservations/useReservationsAPI';
import PopUpContainer from 'app/shareable/PopUpContainer';
import InputSearchBar from './InputSearchBar';
import ListReservations from './ListReservations';

const MIN_SEARCH_LENGTH = 2;

function SearchBarReservations() {
  const [search, setSearch] = useState('');
  const {
    reservationsList,
    pagination,
    resetPagination,
    fetchReservations,
    fetchReservationsNext,
    clearReservationsList,
    isLoading,
    isNotFound,
  } = useReservationsAPI();
  const { debouncedValue, onClearDebounce } = useDebounce(search, 250);
  const HAS_SEARCH_VALUE = debouncedValue.length > MIN_SEARCH_LENGTH;

  const handleChange = useCallback(
    (e) => {
      setSearch(e.target.value);
    },
    [setSearch]
  );

  useEffect(() => {
    if (debouncedValue && search !== debouncedValue) {
      resetPagination();
    }
  }, [debouncedValue, search]);

  useEffect(() => {
    if (HAS_SEARCH_VALUE) {
      fetchReservations(debouncedValue);
    }
  }, [debouncedValue]);

  const resetState = useCallback(() => {
    clearReservationsList();
    onClearDebounce();
    setSearch('');
  }, []);

  const onFetchMore = useCallback(
    async (skip: number) => {
      const PAGE_SIZE = 10;
      await fetchReservationsNext({
        searchText: debouncedValue,
        page: skip / PAGE_SIZE,
      });
    },
    [debouncedValue]
  );

  return (
    <PopUpContainer onOverlayClick={resetState}>
      {({ isVisible, onOpen, onClose }) => (
        <div className="width-500 pos-relative">
          <InputSearchBar
            value={search}
            focus={isVisible}
            onChange={handleChange}
            onFocus={onOpen}
          />

          <ListReservations
            list={reservationsList}
            search={debouncedValue}
            onLoadMore={onFetchMore}
            page={pagination.nextPage}
            isFetching={isLoading}
            isEmpty={isNotFound && !isLoading}
            isFocused={debouncedValue.length <= MIN_SEARCH_LENGTH && !isLoading}
            isVisible={isVisible}
            onClickLink={() => {
              resetState();
              onClose();
            }}
          />
        </div>
      )}
    </PopUpContainer>
  );
}

export default SearchBarReservations;
